import { useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  gridExpandedAF,
  hasKeys,
  mapOpenAF,
  RidesViewTypeEnum,
  useGridExpandedCollapse,
} from "@ryde/shared";
import { deepPurpleMui, greyMui, rydeColors } from "@ryde/theme";

import { usePageContext } from "../../contexts";
import { useRidesNavigation } from "../../navigation";

export const useRidesMapToggleButton = () => {
  const { entity } = usePageContext();
  const { ridesPageViewType } = useRidesNavigation();
  const { expandByIds, collapseAll } = useGridExpandedCollapse();

  const isGridExpanded = useRecoilValue(gridExpandedAF(entity));

  const [isMapOpen, setIsMapOpen] = useRecoilState(
    mapOpenAF(ridesPageViewType ?? RidesViewTypeEnum.GridMultiSelect),
  );

  const [lastExpandedIds, setLastExpandedIds] = useState<Set<number>>(
    new Set(),
  );

  const toggleMapOpen = useEvent(() => {
    if (!isMapOpen) {
      if (hasKeys(isGridExpanded)) {
        const expandedIds = new Set(
          Object.keys(isGridExpanded).map(id => Number(id)),
        );

        setLastExpandedIds(expandedIds);
        collapseAll();
      }

      setIsMapOpen(true);
      return;
    }

    if (lastExpandedIds.size > 0) {
      expandByIds(lastExpandedIds);
      setLastExpandedIds(new Set());
    }

    setIsMapOpen(false);
  });

  const buttonColor = useMemo(() => {
    if (!isMapOpen) {
      return greyMui._600;
    }

    return lastExpandedIds.size > 0 ? rydeColors.orange : deepPurpleMui.A700;
  }, [isMapOpen, lastExpandedIds]);

  return {
    buttonColor,
    toggleMapOpen,
  };
};
